import styled from 'styled-components'
import { Button } from '@stokr/components-library'
import { stoColor, stoMedia, stoRadius, stoSpace, stoType } from 'constants/stoTheme'
import { chartColor } from 'components/ui/chart'

/** The calculator, straight in its section's card: inputs, results, then the offer. */
export const Card = styled.div`
  display: flex;
  flex-direction: column;
`

export const Inputs = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${stoSpace[6]};
  padding-bottom: ${stoSpace[6]};
`

export const Control = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${stoSpace[2]};
`

/** The chart's segmented buttons hug their labels, as on the charts, instead of stretching the column. */
export const TabsSlot = styled.div`
  align-self: flex-start;
  max-width: 100%;
  overflow-x: auto;
`

/** The typed amount and what it gives, side by side; stacked below tablet. */
export const Pair = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${stoSpace[5]};

  ${stoMedia.belowTablet} {
    grid-template-columns: minmax(0, 1fr);
    gap: ${stoSpace[4]};
  }
`

/** The derived figure: the read-only field's look, with its sub-line. */
export const Derived = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 2px;
  min-height: 74px;
  padding: ${stoSpace[3]} 20px;
  border: 1px solid ${stoColor.borderSubtle};
  border-radius: ${stoRadius.lg};
  background: ${stoColor.surfaceAlt};
  overflow-wrap: anywhere;
`

export const DerivedValue = styled.span`
  font-size: 24px;
  font-weight: 700;
  line-height: 32px;
  letter-spacing: 0.5px;
  color: ${stoColor.textPrimary};
`

export const Caption = styled.p<{ $warn?: boolean }>`
  ${stoType.bodySm};
  margin: 0;
  color: ${({ $warn }) => ($warn ? chartColor.negative : stoColor.textSecondary)};
`

export const PriceLine = styled.p`
  ${stoType.bodyMd};
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: ${stoSpace[2]};
  margin: 0;
  padding-top: ${stoSpace[4]};
  border-top: 1px solid ${stoColor.borderSubtle};
  color: ${stoColor.textPrimary};

  strong {
    font-weight: 600;
  }
`

/** Facility, collateral and risk levels: three columns, stacked below desktop. */
export const Results = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  border-top: 1px solid ${stoColor.borderSubtle};

  ${stoMedia.belowDesktop} {
    grid-template-columns: minmax(0, 1fr);
  }
`

export const ResultColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${stoSpace[3]};
  padding: ${stoSpace[6]};

  /* Flush with the section's edges, as the inputs above. */
  &:first-child {
    padding-left: 0;
  }

  &:last-child {
    padding-right: 0;
  }

  & + & {
    border-left: 1px solid ${stoColor.borderSubtle};
  }

  ${stoMedia.belowDesktop} {
    padding: ${stoSpace[6]} 0;

    &:first-child,
    &:last-child {
      padding-left: 0;
      padding-right: 0;
    }

    & + & {
      border-left: none;
      border-top: 1px solid ${stoColor.borderSubtle};
    }
  }
`

export const ResultValue = styled.span<{ $tone?: 'warning' | 'danger' }>`
  ${stoType.bodyMdCallout};
  min-width: 0;
  text-align: right;
  overflow-wrap: anywhere;
  color: ${({ $tone }) =>
    $tone === 'danger' ? chartColor.negative : $tone === 'warning' ? stoColor.warningText : stoColor.textPrimary};
`

/** The note under a result value (`78% LTV`, `Debifi`). */
export const ResultNote = styled.span`
  ${stoType.bodySm};
  display: block;
  font-weight: 400;
  color: ${stoColor.textSecondary};
`

export const Footer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${stoSpace[5]};
  padding: ${stoSpace[5]} ${stoSpace[6]};
  border-radius: ${stoRadius.lg};
  background: ${stoColor.surfaceAlt};

  ${stoMedia.belowTablet} {
    padding: ${stoSpace[4]};
  }
`

export const FooterText = styled.p`
  ${stoType.bodySm};
  flex: 1 1 240px;
  margin: 0;
  color: ${stoColor.textSecondary};
`

/** The library's filled primary button, as a link. `!important`: the global `a { color: inherit }`. */
export const OfferButton = styled(Button)`
  color: ${stoColor.buttonLabelOnFill} !important;
`

export const Disclaimer = styled.p`
  ${stoType.bodySm};
  margin: 0;
  font-style: italic;
  color: ${stoColor.textSecondary};
`
